import { parsePersonDateAsLocal } from "@/shared/utils/formatters";
import type { WillExtraction } from "@/shared/components/document-viewer/WillInsights";

export interface WillValidationWarning {
  section: string;
  field: string;
  message: string;
  severity: "warning" | "error";
}

interface DecedentInfo {
  fullName: string;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
}

function normalizeNameTokens(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[^a-z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean);
}

function namesOverlap(name1: string, name2: string): boolean {
  const tokens1 = normalizeNameTokens(name1);
  const tokens2 = normalizeNameTokens(name2);
  const matchCount = tokens1.filter((t) => tokens2.includes(t)).length;
  return matchCount >= 2 || (tokens1.length === 1 && tokens2.includes(tokens1[0]));
}

export function validateWillExtraction(
  willData: WillExtraction,
  decedent: DecedentInfo | null,
): WillValidationWarning[] {
  const warnings: WillValidationWarning[] = [];

  const bg = willData.document_background;

  // 1. Testator name vs death certificate name
  if (decedent && bg?.testator_name_as_signed && decedent.fullName) {
    const testatorName = bg.testator_name_as_signed;
    const decedentName = decedent.fullName;
    if (!namesOverlap(testatorName, decedentName)) {
      const akaMatch = bg.also_known_as?.some((aka) => namesOverlap(aka, decedentName));
      if (!akaMatch) {
        warnings.push({
          section: "document_background",
          field: "testator_name_as_signed",
          message: `Testator name "${testatorName}" does not match the decedent name "${decedentName}". This may require attorney review.`,
          severity: "warning",
        });
      }
    }
  }

  // 2. Date signed validation
  if (decedent && bg?.date_signed) {
    const dateSigned = parsePersonDateAsLocal(bg.date_signed);
    if (dateSigned) {
      if (decedent.dateOfBirth) {
        const dob = parsePersonDateAsLocal(decedent.dateOfBirth);
        if (dob && dateSigned < dob) {
          warnings.push({
            section: "document_background",
            field: "date_signed",
            message: "Will was signed before the testator's date of birth.",
            severity: "error",
          });
        }
      }
      if (decedent.dateOfDeath) {
        const dod = parsePersonDateAsLocal(decedent.dateOfDeath);
        if (dod && dateSigned > dod) {
          warnings.push({
            section: "document_background",
            field: "date_signed",
            message: "Will was signed after the date of death.",
            severity: "error",
          });
        }
      }
    }
  }

  // 3. Witness vs beneficiary overlap
  const witnessNames = (willData.witness_info || []).map((w) => w.name).filter(Boolean);
  const allBeneficiaryNames: string[] = [];

  for (const bequest of willData.specific_bequests || []) {
    for (const b of bequest.beneficiaries || []) {
      if (b.name) allBeneficiaryNames.push(b.name);
    }
    for (const cb of bequest.contingent_beneficiaries || []) {
      if (cb.name) allBeneficiaryNames.push(cb.name);
    }
  }
  for (const b of willData.residuary_estate?.beneficiaries || []) {
    if (b.name) allBeneficiaryNames.push(b.name);
    for (const cb of b.contingent_beneficiaries || []) {
      if (cb.name) allBeneficiaryNames.push(cb.name);
    }
  }

  for (const witnessName of witnessNames) {
    const overlapping = allBeneficiaryNames.find((bn) => namesOverlap(witnessName, bn));
    if (overlapping) {
      warnings.push({
        section: "signing_and_witnesses",
        field: "witness_info",
        message: `Witness "${witnessName}" appears to also be a beneficiary. This may require attorney review.`,
        severity: "warning",
      });
    }
  }

  // 4. Page continuity
  if (bg?.page_continuity_flag === false) {
    warnings.push({
      section: "document_background",
      field: "page_continuity_flag",
      message: "Page numbering may not be continuous. Verify all pages are present.",
      severity: "warning",
    });
  }

  // 5. Text after signature
  if (willData.signing_and_witnesses?.text_after_signature) {
    warnings.push({
      section: "signing_and_witnesses",
      field: "text_after_signature",
      message: "There is text after the testator's signature. This may indicate additions that were not properly executed.",
      severity: "warning",
    });
  }

  return warnings;
}

export function getPropertyScope(willData: WillExtraction): string | null {
  const hasRealProperty =
    (willData.real_property && willData.real_property.length > 0) ||
    willData.specific_bequests?.some((b) => b.category === "real_estate");

  const hasPersonalProperty = willData.specific_bequests?.some(
    (b) => b.category && b.category !== "real_estate",
  );

  if (hasRealProperty && hasPersonalProperty) return "Real and personal property";
  if (hasRealProperty) return "Real property";
  if (hasPersonalProperty) return "Personal property";
  return null;
}
